import { saveChatMessage, saveAuditLog } from '../firebase.js';

export class InteractiveWidgets {
  constructor() {
    this.initTerminalSimulator();
    this.initAppChatbotSimulator();
    this.initCybersecurityScanner();
  }

  init3DTiltCards() {
    const cards = document.querySelectorAll('.glass-card, .service-card, .academic-stage-card');

    cards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -8;
        const rotateY = ((x - centerX) / centerX) * 8;

        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;

        // Specular glow highlight position
        card.style.setProperty('--mouse-x', `${(x / rect.width) * 100}%`);
        card.style.setProperty('--mouse-y', `${(y / rect.height) * 100}%`);
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateZ(0px)';
      });
    });
  }

  initTerminalSimulator() {
    const terminalOutput = document.getElementById('terminal-response');
    const executeBtn = document.getElementById('terminal-run-btn');
    if (!executeBtn || !terminalOutput) return;

    const scripts = [
      "Running RAG Vector Indexer on 500,000 documents...",
      "Executing Zero-Trust mTLS Network Handshake across Multi-Cloud...",
      "Building Flutter & React Native AI Chat Engine (Dart/TypeScript)...",
      "Validating PhD Experimental Ablation Model on 8x H100 GPUs...",
      "Optimizing PyTorch TensorRT Latency to 3.8ms...",
      "✔ All 25,000 Neural & Cyber Defense Workflows Active. SLA: 99.99%"
    ];

    let scriptIndex = 0;

    executeBtn.addEventListener('click', () => {
      terminalOutput.innerHTML = '<span class="pulse-dot"></span> Executing pipeline...';
      setTimeout(() => {
        terminalOutput.textContent = scripts[scriptIndex];
        scriptIndex = (scriptIndex + 1) % scripts.length;
      }, 500);
    });
  }

  initAppChatbotSimulator() {
    const chatBody = document.getElementById('demo-chat-body');
    const chatInput = document.getElementById('demo-chat-input');
    const sendBtn = document.getElementById('demo-chat-send');
    const chipBtns = document.querySelectorAll('.chip-btn');
    const flutterTab = document.getElementById('tab-flutter');
    const reactTab = document.getElementById('tab-react');
    const appFrameworkBadge = document.getElementById('app-framework-badge');

    if (!chatBody || !chatInput || !sendBtn) return;

    let currentFramework = 'Flutter';

    if (flutterTab && reactTab && appFrameworkBadge) {
      flutterTab.addEventListener('click', () => {
        flutterTab.classList.add('active');
        reactTab.classList.remove('active');
        currentFramework = 'Flutter';
        appFrameworkBadge.textContent = 'Flutter Engine v3.19 • Dart Core';
        this.appendMessage('bot', 'Switched to <strong>Flutter Architecture</strong>! Running native Skia/Impeller 120fps engine with on-device LLM cache.');
      });

      reactTab.addEventListener('click', () => {
        reactTab.classList.add('active');
        flutterTab.classList.remove('active');
        currentFramework = 'React';
        appFrameworkBadge.textContent = 'React Native 0.74 • Hermes Engine';
        this.appendMessage('bot', 'Switched to <strong>React Native Architecture</strong>! Running Hermes JS bytecode engine with WebSocket streaming AI.');
      });
    }

    const botResponses = {
      'cloud': "🛡️ <strong>Zero-Trust Analysis Complete:</strong> Analyzed cloud VPC peering & IAM roles. Found 0 open high-risk ports. Automated mTLS encryption enabled across all microservices.",
      'phd': "🎓 <strong>PhD Research Advisory:</strong> For novel Transformer efficiency, we suggest formulating a dynamic sparse-attention matrix combined with 4-bit AWQ quantization to benchmark against Llama-3.",
      'app': "📱 <strong>Flutter & React AI Chatbot:</strong> App is integrated with hybrid on-device SQLite cache + Claude/OpenAI streaming endpoints with sub-180ms time-to-first-token.",
      'default': "✨ <strong>Wah AI Assistant:</strong> We build world-class Flutter/React apps with embedded AI, autonomous agents, and enterprise cybersecurity. How can we assist your project?"
    };

    const handleSendMessage = (text) => {
      const msg = text.trim();
      if (!msg) return;

      this.appendMessage('user', msg);
      chatInput.value = '';

      // Record user message in Firebase Firestore
      saveChatMessage('user', msg, currentFramework);

      // Simulated streaming response
      setTimeout(() => {
        let reply = botResponses.default;
        const lower = msg.toLowerCase();
        if (lower.includes('cloud') || lower.includes('security') || lower.includes('network')) {
          reply = botResponses.cloud;
        } else if (lower.includes('phd') || lower.includes('masters') || lower.includes('research') || lower.includes('thesis')) {
          reply = botResponses.phd;
        } else if (lower.includes('app') || lower.includes('flutter') || lower.includes('react') || lower.includes('chat')) {
          reply = botResponses.app;
        }
        this.appendMessage('bot', reply);
        saveChatMessage('bot', reply, currentFramework);
      }, 600);
    };

    sendBtn.addEventListener('click', () => {
      handleSendMessage(chatInput.value);
    });

    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSendMessage(chatInput.value);
      }
    });

    chipBtns.forEach((chip) => {
      chip.addEventListener('click', () => {
        const prompt = chip.getAttribute('data-prompt') || chip.textContent;
        handleSendMessage(prompt);
      });
    });
  }

  appendMessage(sender, text) {
    const chatBody = document.getElementById('demo-chat-body');
    if (!chatBody) return;

    const bubble = document.createElement('div');
    bubble.className = `chat-bubble ${sender}`;

    if (sender === 'user') {
      // Escape raw HTML entities to prevent client-side XSS injection
      bubble.textContent = text;
    } else {
      // Controlled system responses can render secure markup
      bubble.innerHTML = text;
    }

    chatBody.appendChild(bubble);
    chatBody.scrollTop = chatBody.scrollHeight;
  }

  initCybersecurityScanner() {
    const scanBtn = document.getElementById('cyber-scan-btn');
    const scanOutput = document.getElementById('cyber-scan-output');
    if (!scanBtn || !scanOutput) return;

    const logs = [
      "<span class='info'>[0.12s]</span> Initiating Zero-Trust perimeter handshake...",
      "<span class='info'>[0.34s]</span> Probing 65,535 TCP/UDP ingress ports via distributed sensor...",
      "<span class='success'>[0.68s]</span> Next-Gen Firewall (NGFW) & WAF Active. 0 open unauthorized ports.",
      "<span class='success'>[0.95s]</span> SD-WAN IPSec VPN tunnels verified with AES-256-GCM encryption.",
      "<span class='success'>[1.20s]</span> SIEM / SOC Telemetry: 100% Threat Immunity. SOC 2 & HIPAA Compliant."
    ];

    scanBtn.addEventListener('click', () => {
      scanBtn.disabled = true;
      scanBtn.textContent = '⚡ Scanning Network Defense Layers...';
      scanOutput.innerHTML = '';

      logs.forEach((log, index) => {
        setTimeout(() => {
          const line = document.createElement('div');
          line.innerHTML = log;
          scanOutput.appendChild(line);

          if (index === logs.length - 1) {
            scanBtn.disabled = false;
            scanBtn.textContent = '▶ Re-Run Deep Defense Audit';
          }
        }, (index + 1) * 350);
      });
    });
  }
}

